import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import { useToast } from '../../context/ToastContext';

const AccountList = () => {
  const toast = useToast();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');

  // Modal State for Add / Edit
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    accountName: '',
    accountType: 'BOTH',
    address: '',
    mobileNo: '',
    disc: 0,
    openingBalance: 0,
    balanceDate: new Date().toISOString().slice(0, 10)
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(`/accounts?search=${search}&type=${typeFilter}`);
      if (res.success) {
        setAccounts(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [typeFilter]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchAccounts();
  };

  const handleOpenAdd = () => {
    setEditId(null);
    setFormData({
      accountName: '',
      accountType: 'BOTH',
      address: '',
      mobileNo: '',
      disc: 0,
      openingBalance: 0,
      balanceDate: new Date().toISOString().slice(0, 10)
    });
    setFormError('');
    setShowModal(true);
  };

  const handleOpenEdit = async (id) => {
    setEditId(id);
    setFormError('');
    try {
      const res = await axiosClient.get(`/accounts/${id}`);
      if (res.success) {
        const a = res.data;
        setFormData({
          accountName: a.AccountName || '',
          accountType: a.AccountType || 'BOTH',
          address: a.Address || '',
          mobileNo: a.MobileNo || '',
          disc: a.disc || 0,
          openingBalance: a.openingBalance || 0,
          balanceDate: a.openingBalanceDate || new Date().toISOString().slice(0, 10)
        });
        setShowModal(true);
      }
    } catch (err) {
      alert(err.message || 'Failed to fetch account details.');
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      if (editId) {
        await axiosClient.put(`/accounts/${editId}`, formData);
        toast.success(`Account "${formData.accountName}" updated successfully.`);
      } else {
        await axiosClient.post('/accounts', formData);
        toast.success(`Account "${formData.accountName}" created successfully.`);
      }
      setShowModal(false);
      fetchAccounts();
    } catch (err) {
      const errMsg = err.message || 'Error saving account.';
      setFormError(errMsg);
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete account "${name}"?`)) {
      try {
        await axiosClient.delete(`/accounts/${id}`);
        toast.success(`Account "${name}" deleted successfully.`);
        fetchAccounts();
      } catch (err) {
        toast.error(err.message || 'Error deleting account.');
      }
    }
  };

  return (
    <div className="container-fluid p-0">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="fw-bold mb-0 text-dark">
            <i className="bi bi-people-fill text-primary me-2"></i> Account Master (Unified)
          </h3>
          <p className="text-muted small mb-0">Manage Customers, Suppliers, and Traders with integrated balances</p>
        </div>
        <button className="btn btn-ocean d-flex align-items-center gap-2" onClick={handleOpenAdd}>
          <i className="bi bi-person-plus-fill"></i> Add New Account
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body p-3">
          <form className="row g-2 align-items-center" onSubmit={handleSearchSubmit}>
            <div className="col-12 col-md-5">
              <div className="input-group">
                <span className="input-group-text bg-light"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, mobile, or address..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="col-6 col-md-3">
              <select
                className="form-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="ALL">All Account Types</option>
                <option value="CUSTOMER">Customers</option>
                <option value="SUPPLIER">Suppliers</option>
                <option value="BOTH">Both (Customer & Supplier)</option>
              </select>
            </div>

            <div className="col-6 col-md-2">
              <button type="submit" className="btn btn-primary w-100">Search</button>
            </div>

            <div className="col-12 col-md-2 text-end">
              <span className="badge bg-light text-dark border p-2">Total: {accounts.length}</span>
            </div>
          </form>
        </div>
      </div>

      {/* Accounts List Table */}
      <div className="card shadow-sm border-0">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '60px' }}>#</th>
                  <th>Account Name</th>
                  <th>Type</th>
                  <th>Mobile Number</th>
                  <th>Address</th>
                  <th className="text-center">Discount %</th>
                  <th className="text-center" style={{ width: '200px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <div className="spinner-border text-primary" role="status"></div>
                    </td>
                  </tr>
                ) : accounts.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      No accounts found matching your criteria.
                    </td>
                  </tr>
                ) : (
                  accounts.map((a, idx) => (
                    <tr key={a.Id}>
                      <td className="text-muted small">{idx + 1}</td>
                      <td>
                        <strong className="text-dark">{a.AccountName}</strong>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            a.AccountType === 'CUSTOMER'
                              ? 'bg-info text-dark'
                              : a.AccountType === 'SUPPLIER'
                              ? 'bg-warning text-dark'
                              : 'bg-primary'
                          }`}
                        >
                          {a.AccountType}
                        </span>
                      </td>
                      <td>{a.MobileNo || '-'}</td>
                      <td className="small text-muted">{a.Address || '-'}</td>
                      <td className="text-center">{a.disc ? `${a.disc}%` : '-'}</td>
                      <td className="text-center">
                        <div className="btn-group btn-group-sm">
                          <Link
                            to={`/reports/statement?accountId=${a.Id}`}
                            className="btn btn-outline-info"
                            title="View Statement"
                          >
                            <i className="bi bi-journal-text"></i> Ledger
                          </Link>
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleOpenEdit(a.Id)}
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(a.Id, a.AccountName)}
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-dark text-white">
                <h5 className="modal-title">
                  <i className="bi bi-person-gear me-2"></i>
                  {editId ? 'Edit Account' : 'Create New Account'}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>

              <form onSubmit={handleFormSubmit}>
                <div className="modal-body p-4">
                  {formError && <div className="alert alert-danger py-2 small">{formError}</div>}

                  <div className="mb-3">
                    <label className="form-label fw-semibold small">Account Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      required
                      placeholder="e.g. SANDEEP TRADERS"
                      value={formData.accountName}
                      onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                    />
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <label className="form-label fw-semibold small">Account Type</label>
                      <select
                        className="form-select"
                        value={formData.accountType}
                        onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                      >
                        <option value="BOTH">Both (Customer & Supplier)</option>
                        <option value="CUSTOMER">Customer</option>
                        <option value="SUPPLIER">Supplier</option>
                      </select>
                    </div>

                    <div className="col-6">
                      <label className="form-label fw-semibold small">Default Discount %</label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        value={formData.disc}
                        onChange={(e) => setFormData({ ...formData, disc: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small">Mobile / Contact Number</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. 9820000000"
                      value={formData.mobileNo}
                      onChange={(e) => setFormData({ ...formData, mobileNo: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small">Address</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder="Market / Shop address..."
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    ></textarea>
                  </div>

                  <div className="row g-2">
                    <div className="col-6">
                      <label className="form-label fw-semibold small">Opening Balance (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        placeholder="Positive = Receivable, Negative = Payable"
                        value={formData.openingBalance}
                        onChange={(e) => setFormData({ ...formData, openingBalance: e.target.value })}
                      />
                      <div className="form-text small">+ve: Customer owes, -ve: We owe</div>
                    </div>

                    <div className="col-6">
                      <label className="form-label fw-semibold small">Balance Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.balanceDate}
                        onChange={(e) => setFormData({ ...formData, balanceDate: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Account'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountList;
